import {
    Block,
    type Change,
    isTextRangeSelection,
    type NodeType,
    type Value,
} from '@gitbook/slate';

import { createParagraph } from '../paragraph';
import { BLOCK_CODE, BLOCK_TABS, BLOCK_TABS_ITEM } from '../types';

/**
 * Create a tabs block.
 */
export function createTabs(
    nodes: Block[] = [createTabsItem('First Tab'), createTabsItem('Second Tab')],
    fullWidth?: boolean
): Block {
    return Block.create({
        type: BLOCK_TABS,
        data: {
            ...(typeof fullWidth !== 'undefined' ? { fullWidth } : {}),
        },
        nodes,
    });
}

/**
 * Create a tabs item block.
 */
export function createTabsItem(title: string, nodes: Block[] = [createParagraph()]): Block {
    return Block.create({
        type: BLOCK_TABS_ITEM,
        data: { title },
        nodes,
    });
}

/**
 * Get the currently selected tab.
 */
export function getCurrentTab(value: Value) {
    const { selection, document } = value;
    const startKey = isTextRangeSelection(selection) ? selection.startKey : selection.anchorBlock;
    if (!startKey) {
        return null;
    }
    const tabsItem = document.getClosest(
        startKey,
        (node: NodeType) => node.object === 'block' && node.type === BLOCK_TABS_ITEM
    );
    return (tabsItem as Block) ?? null;
}

/**
 * Get the previous tab.
 */
export function getPreviousTab(value: Value, currentTab: Block): Block | null {
    const tabContainer = value.document.getParent(currentTab.key);
    if (!tabContainer) {
        return null;
    }

    // @ts-expect-error
    const previousTabIndex = tabContainer.nodes.indexOf(currentTab) - 1;
    const previousTab = previousTabIndex >= 0 ? tabContainer.nodes.get(previousTabIndex) : null;

    // @ts-expect-error
    return previousTab;
}

/**
 * Get the next tab.
 */
export function getNextTab(value: Value, currentTab: Block): Block | null {
    const tabContainer = value.document.getParent(currentTab.key);
    if (!tabContainer) {
        return null;
    }

    // @ts-expect-error
    const nextTabIndex = tabContainer.nodes.indexOf(currentTab) + 1;

    // @ts-expect-error
    return tabContainer.nodes.get(nextTabIndex, null);
}

/**
 * Does the tab only contain a single code block?
 */
export function isCodeTab(block: Block): boolean {
    return block.nodes.size === 1 && block.nodes.first()?.type === BLOCK_CODE;
}

/**
 * Removes a Tab from a TabsBlock.
 */
export function removeTab(change: Change, nodeKey: string): Change | null {
    const { value } = change;

    const currentTab = getCurrentTab(value);
    if (currentTab && currentTab.key !== nodeKey) {
        return change.removeNodeByKey(nodeKey);
    }

    const tab = value.document.getNode(nodeKey);
    if (!tab || tab.object !== 'block') {
        return null;
    }

    // Check for a previous tab.
    const previousTab = getPreviousTab(value, tab);
    if (previousTab) {
        change.collapseToEndOf(previousTab);
        return change.removeNodeByKey(tab.key);
    }

    // No previous? Check for a next tab.
    const nextTab = getNextTab(value, tab);
    if (nextTab) {
        change.collapseToStartOf(nextTab);
        return change.removeNodeByKey(tab.key);
    }

    // No next? Delete whole container.
    const blockTabs = value.document.getParent(tab.key);
    if (blockTabs) {
        return change.removeNodeByKey(blockTabs.key);
    }

    return null;
}
